import posthog from "posthog-js";

/**
 * Env: `NEXT_PUBLIC_POSTHOG_KEY` (required). Optional: `NEXT_PUBLIC_POSTHOG_API_HOST` (ingestion;
 * defaults to https://i.agnexus.ai). `NEXT_PUBLIC_POSTHOG_DEBUG` — see isPosthogDebugEnabled.
 *
 * Verbose PostHog client logging (browser console).
 * - Local: `next dev` → NODE_ENV is development.
 * - Vercel preview / custom env (e.g. staging): VERCEL_TARGET_ENV is not `production`.
 * - Override: NEXT_PUBLIC_POSTHOG_DEBUG=true|false (false wins to force off on staging).
 */
function isPosthogDebugEnabled(): boolean {
  const flag = process.env.NEXT_PUBLIC_POSTHOG_DEBUG;
  if (flag === "false" || flag === "0") return false;
  if (flag === "true" || flag === "1") return true;
  if (process.env.NODE_ENV === "development") return true;
  if (process.env.VERCEL === "1") {
    const target = process.env.VERCEL_TARGET_ENV;
    if (target && target !== "production") return true;
  }
  return false;
}

/** Ingestion reverse proxy (e.g. Cloudflare → PostHog EU). Override per env with NEXT_PUBLIC_POSTHOG_API_HOST. */
const posthogApiHost =
  (process.env.NEXT_PUBLIC_POSTHOG_API_HOST ?? "https://i.agnexus.ai").replace(
    /\/$/,
    ""
  );

const posthogKey = (process.env.NEXT_PUBLIC_POSTHOG_KEY || "").trim();
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: posthogApiHost,
    ui_host: "https://eu.posthog.com",
    defaults: "2026-01-30",
    capture_exceptions: true,
    // Customer text never reaches analytics: chat answers, automation output,
    // client names and notes are on almost every screen, so no text at all.
    // Autocapture keeps the element and the page, not what it says…
    mask_all_text: true,
    mask_all_element_attributes: true,
    // …and replays keep layout and clicks with every word masked.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
    },
    // Web vitals for the redesign baseline (#491): numbers only, no text.
    capture_performance: { web_vitals: true },
    debug: isPosthogDebugEnabled(),
  });
  // The new app (web/) shares the project: every event says which app and
  // environment it came from, so the two compare cleanly (#491).
  posthog.register({
    app: "frontend",
    environment:
      process.env.NEXT_PUBLIC_APP_ENV ||
      process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV ||
      process.env.NEXT_PUBLIC_VERCEL_ENV ||
      "development",
  });
}